import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { usePersistedSearchkitVariables } from '../common/components/filtering/filters';
import PersonSelector from '../common/components/PersonSelector';
import { FORM_WIDGET_COMPONENTS, METADATA_NAMES } from '../common/form/formConstants';
import { FormContainer } from '../common/form/FormContainer';
import { FormContext } from '../common/form/FormContext';
import FormItem from '../common/form/FormItem';
import FormPanel from '../common/form/FormPanel';
import FormWidget from '../common/form/FormWidget';
import { useI18n } from '../common/i18n/I18n';
import { ORGANIZATION_EDITOR_FIELD_NAMES } from './common/organizationConstants';

interface QuickAddOrganizationFormProps {
    checkShowLocation: () => Boolean;
    setIsSaveDisabled?: Dispatch<SetStateAction<boolean>>;
    id?: string;
    keyOrganizationFormFields?: boolean;
}
export const QuickAddOrganizationForm = ({
    id,
    setIsSaveDisabled,
    checkShowLocation,
    keyOrganizationFormFields = false
}: QuickAddOrganizationFormProps) => {
    const { validation, state, setState } = useContext(FormContext);
    const { variables } = usePersistedSearchkitVariables(id);
    const nameFieldLabel = keyOrganizationFormFields
        ? useI18n('key.organization.field.name')
        : useI18n('organization.field.name');
    const typeFieldLabel = keyOrganizationFormFields
        ? useI18n('key.organization.field.type')
        : useI18n('organization.field.type');
    const categoryFieldLabel = useI18n('organization.field.category');
    const roleFieldLabel = useI18n('organization.field.organizationRole');
    const conatctLabel = useI18n('common.contact');
    const contactPlaceholder = useI18n('organization.contact.placeholder');
    const noContactOptionsText = useI18n('organization.search.contact');
    const locationFieldLabel = useI18n('organization.field.location');

    useEffect(() => {
        setIsSaveDisabled?.(!validation?.isValid);
    }, [setIsSaveDisabled, validation]);

    useEffect(() => {
        if (variables.query) {
            setState({ ...state, publicName: variables.query });
        }
    }, [variables.query]);

    return (
        <FormPanel>
            <FormContainer>
                <FormItem>
                    <FormWidget name={ORGANIZATION_EDITOR_FIELD_NAMES.name} label={nameFieldLabel} />
                </FormItem>
                <FormItem>
                    <FormWidget
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        name={ORGANIZATION_EDITOR_FIELD_NAMES.type}
                        label={typeFieldLabel}
                        metadataName={METADATA_NAMES.organizationType}
                        limitTags={3}
                        compressed
                        multiple
                    />
                </FormItem>
                {keyOrganizationFormFields && (
                    <>
                        <FormItem>
                            <FormWidget
                                component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                                name={ORGANIZATION_EDITOR_FIELD_NAMES.category}
                                label={categoryFieldLabel}
                                metadataName={METADATA_NAMES.category}
                                compressed
                                multiple
                            />
                        </FormItem>
                        <FormWidget
                            component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                            name={ORGANIZATION_EDITOR_FIELD_NAMES.roles}
                            label={roleFieldLabel}
                            metadataName={METADATA_NAMES.organizationRole}
                            compressed
                        />
                    </>
                )}
                {!keyOrganizationFormFields && (
                    <FormItem>
                        <PersonSelector
                            data-testid={'quickOrgContact'}
                            name={ORGANIZATION_EDITOR_FIELD_NAMES.contact}
                            label={conatctLabel}
                            multiple
                            autoFocus={false}
                            placeholder={contactPlaceholder}
                            noOptionsText={noContactOptionsText}
                        />
                    </FormItem>
                )}
                {checkShowLocation() && (
                    <FormItem>
                        <FormWidget
                            component="Location"
                            label={locationFieldLabel}
                            name={ORGANIZATION_EDITOR_FIELD_NAMES.locations}
                            appendTimeZone
                        />
                    </FormItem>
                )}
            </FormContainer>
        </FormPanel>
    );
};
